import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * Axios instance for the ProfitVault API. Every request attaches the Shopify
 * App Bridge session token as a Bearer header so the backend can verify the
 * shop (see VerifyShopifySession middleware).
 */
export const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Fallback holder for a manually-supplied token (tests / non-embedded dev).
let sessionToken: string | null = null;

export function setSessionToken(token: string | null): void {
  sessionToken = token;
}

// App Bridge v4 exposes a global `shopify` with an async idToken() that returns
// a fresh (short-lived) session token. Prefer it when embedded.
interface AppBridgeGlobal {
  idToken?: () => Promise<string>;
}

client.interceptors.request.use(async (config) => {
  const bridge = (window as unknown as { shopify?: AppBridgeGlobal }).shopify;
  let token = sessionToken;

  if (bridge?.idToken) {
    try {
      token = await bridge.idToken();
    } catch {
      // fall back to the stored token
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    // Surface a clean message; caller decides how to display it.
    const message =
      error?.response?.data?.error ??
      error?.response?.data?.message ??
      error.message ??
      'Request failed';
    return Promise.reject(new Error(message));
  },
);
